import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface DateFieldProps {
  /** Controlled value in ISO `YYYY-MM-DD` form (empty string when unset). */
  value: string;
  /** Called with the new ISO `YYYY-MM-DD` value (empty string when cleared). */
  onChange: (value: string) => void;
  /** Earliest selectable date, ISO `YYYY-MM-DD`. */
  min?: string;
  /** Latest selectable date, ISO `YYYY-MM-DD`. */
  max?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Native autocomplete token, e.g. `"bday"` for a date-of-birth field. */
  autoComplete?: string;
  'aria-label'?: string;
}

/**
 * The single date picker used everywhere the app collects a calendar date --
 * signup DOB (`LemonadeOnboarding`) and the `date` question type in the
 * profile / survey / campaign wizards.
 *
 * It delegates to the native `<input type="date">`, so on touch devices the OS
 * date picker is used (iOS wheel spinner / Android calendar dialog). That is
 * the most reliable mobile DOB UX and -- unlike a Radix Select or a popover
 * calendar -- it cannot overflow or escape the viewport (the original signup
 * blocker, REK-1649). The value is always ISO `YYYY-MM-DD`, which is exactly
 * what every caller persists, so swapping in this control changes no stored
 * data shape.
 */
export function DateField({
  value,
  onChange,
  min,
  max,
  disabled,
  className,
  id,
  autoComplete,
  'aria-label': ariaLabel,
}: DateFieldProps) {
  return (
    <Input
      type="date"
      id={id}
      aria-label={ariaLabel}
      autoComplete={autoComplete}
      value={value}
      min={min}
      max={max}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      // The base Input focus style is an outset ring (`ring-2` + `ring-offset-2`,
      // i.e. ~4px outside the border box). Inside an `overflow-y-auto` scroll
      // container (the wizards) the horizontal axis is also clipped, so that ring
      // gets cut off on the left/right edges. Swap it for an inset border-color
      // focus indicator -- the same approach the wizards' text inputs already use
      // -- so the focus state is fully visible in every container.
      className={cn(
        'block w-full focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-primary',
        className,
      )}
    />
  );
}

/** Format a `Date` as an ISO `YYYY-MM-DD` string for `min` / `max` bounds. */
export function toISODateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * ISO `min`/`max` for the standard 18-85 date-of-birth eligibility window
 * (latest = 18 years ago, earliest = 85 years ago). Used by every DOB/date
 * surface so the bounds stay identical.
 */
export function getDobBounds(now: Date = new Date()): { min: string; max: string } {
  return {
    max: toISODateString(new Date(now.getFullYear() - 18, now.getMonth(), now.getDate())),
    min: toISODateString(new Date(now.getFullYear() - 85, now.getMonth(), now.getDate())),
  };
}

/**
 * Whether an ISO `YYYY-MM-DD` value is within the inclusive `[min, max]` range.
 * The native `<input type="date">` `min`/`max` attributes only constrain the
 * picker UI, not manually-typed values, so callers must re-check before submit.
 * ISO date strings sort lexicographically in chronological order, so plain
 * string comparison is correct here.
 */
export function isWithinDateBounds(iso: string, min?: string, max?: string): boolean {
  if (!iso) return false;
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  return true;
}
